/**
 * Dailies Subscribe & Save selector.
 *
 * Toggle between subscribe and one-time. On change:
 *   - Updates the `data-selected` attribute (CSS reads this for the active card styling)
 *   - Writes/removes a `selling_plan` hidden input on every product form in the document,
 *     so the next add-to-cart picks up (or drops) the subscription regardless of which
 *     ATC block submits it.
 *   - Broadcasts `dailies:purchase-mode` so other blocks can react if needed.
 */
class DailiesSubscribeSave extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-mode-btn]').forEach((btn) => {
      btn.addEventListener('click', () => this.#select(btn.dataset.modeBtn));
    });

    // Sync immediately so the initial selection is reflected on the product form
    this.#syncForms(this.dataset.selected || 'subscribe');
    this.#broadcast(this.dataset.selected || 'subscribe');
  }

  get sellingPlanId() {
    return this.dataset.sellingPlanId;
  }

  #select(mode) {
    if (mode !== 'subscribe' && mode !== 'onetime') return;
    if (this.dataset.selected === mode) return;

    this.dataset.selected = mode;
    this.querySelectorAll('[data-mode-btn]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.modeBtn === mode ? 'true' : 'false');
    });

    this.#syncForms(mode);
    this.#broadcast(mode);
  }

  /**
   * Find every product form and write/remove a `name="selling_plan"` hidden input
   * to match the chosen mode. The submission then carries (or omits) the selling plan
   * automatically — works with any ATC block (buy-buttons, dailies-cta, sticky ATC).
   */
  #syncForms(mode) {
    const wantSub = mode === 'subscribe' && this.sellingPlanId;
    const forms = document.querySelectorAll('form[action*="/cart/add"]');

    forms.forEach((form) => {
      let input = form.querySelector('input[name="selling_plan"]');

      if (wantSub) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          form.appendChild(input);
        }
        input.value = this.sellingPlanId;
      } else if (input) {
        input.remove();
      }
    });
  }

  #broadcast(mode) {
    document.dispatchEvent(
      new CustomEvent('dailies:purchase-mode', {
        bubbles: true,
        detail: { mode, sellingPlanId: this.sellingPlanId, source: this },
      })
    );
  }
}

if (!customElements.get('dailies-subscribe-save')) {
  customElements.define('dailies-subscribe-save', DailiesSubscribeSave);
}
